import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  BaseRequest,
  ChangeFullNameEmailRequest,
  ChangeUserPasswordRequest,
  ConfirmEmailRequest,
  ForgotPasswordEmailRequest,
  GoogleAuthOneTapRequest,
  GoogleTokenTwoFARequest,
  ReceiveRemindersRequest,
  ResetPasswordRequest,
  SigninRequest,
  SignupRequest,
  SignupRequestExtended,
  TelegramAuthRequestExtended,
  UpdateCheckModeRequest,
  UpdateLocaleRequest,
  User,
  VerifyRecoveryTokenRequest,
} from '@/api';
import { ErrorCodeEnum } from '@/config';
import { UserData } from '../models';

export interface UserSliceState {
  data: UserData;
  loading: boolean;
  suspense: boolean;
  error: string | null;
  errorCode: ErrorCodeEnum | null;
  pendingGoogleToken: string | null;
}

const initialState: UserSliceState = {
  data: null,
  loading: false,
  suspense: true,
  error: null,
  errorCode: null,
  pendingGoogleToken: null,
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    check: (state) => state,
    current: (state) => state,
    signup: (state, action: PayloadAction<SignupRequestExtended>) => ({
      ...state,
      error: null,
      loading: true,
    }),
    signin: (state, action: PayloadAction<SigninRequest>) => ({
      ...state,
      error: null,
      loading: true,
    }),
    googleAuth: (state, action: PayloadAction<GoogleTokenTwoFARequest>) => ({
      ...state,
      error: null,
      loading: true,
    }),
    telegramAuth: (
      state,
      action: PayloadAction<TelegramAuthRequestExtended>,
    ) => ({
      ...state,
      error: null,
      loading: true,
    }),
    googleAuthOneTap: (
      state,
      action: PayloadAction<GoogleAuthOneTapRequest>,
    ) => ({
      ...state,
      error: null,
      loading: true,
    }),
    signout: (state) => ({
      ...state,
      error: null,
      loading: true,
    }),
    confirmEmail: (state, action: PayloadAction<ConfirmEmailRequest>) => ({
      ...state,
      error: null,
      loading: true,
    }),
    forgotPasswordEmail: (
      state,
      action: PayloadAction<ForgotPasswordEmailRequest>,
    ) => ({
      ...state,
      error: null,
      loading: true,
    }),
    verifyRecoveryToken: (
      state,
      action: PayloadAction<VerifyRecoveryTokenRequest>,
    ) => ({
      ...state,
      error: null,
      loading: true,
    }),
    resetPassword: (state, action: PayloadAction<ResetPasswordRequest>) => ({
      ...state,
      error: null,
      loading: true,
    }),
    changeFullNameEmail: (
      state,
      action: PayloadAction<ChangeFullNameEmailRequest>,
    ) => ({
      ...state,
      error: null,
      loading: true,
    }),
    updateLocale: (state, action: PayloadAction<UpdateLocaleRequest>) => state,
    changeUserPassword: (
      state,
      action: PayloadAction<ChangeUserPasswordRequest>,
    ) => ({
      ...state,
      error: null,
      loading: true,
    }),
    changeReceiveReminders: (
      state,
      action: PayloadAction<ReceiveRemindersRequest>,
    ) => ({
      ...state,
      error: null,
      loading: true,
    }),
    setData: (state, action: PayloadAction<UserData>) => ({
      ...state,
      data: action.payload,
    }),
    setBalance: (state, action: PayloadAction<number>) => {
      if (state.data === null) {
        return state;
      }

      return {
        ...state,
        data: {
          ...state.data,
          extBalance: action.payload,
        },
      };
    },
    confirmTerms: (state, action: PayloadAction<BaseRequest>) => ({
      ...state,
      error: null,
      loading: true,
    }),
    setSuspense: (state, action: PayloadAction<boolean>) => ({
      ...state,
      suspense: action.payload,
    }),
    setLoading: (state, action: PayloadAction<boolean>) => ({
      ...state,
      loading: action.payload,
    }),
    setError: (state, action: PayloadAction<string | null>) => ({
      ...state,
      error: action.payload,
    }),
    setErrorCode: (state, action: PayloadAction<ErrorCodeEnum | null>) => ({
      ...state,
      errorCode: action.payload,
    }),
    setPendingGoogleToken: (state, action: PayloadAction<string | null>) => ({
      ...state,
      pendingGoogleToken: action.payload,
    }),
    updateCheckMode: (
      state,
      action: PayloadAction<UpdateCheckModeRequest>,
    ) => ({
      ...state,
      error: null,
      loading: true,
    }),
    updateAttribute: (state, action: PayloadAction<Partial<User>>) => {
      const { data } = state;

      if (data) {
        const { payload } = action;
        const keys = Object.keys(payload);

        keys.forEach((key) => {
          const attribute = key as keyof UserData;
          data[attribute] = payload[attribute];
        });
      }
    },
    discardAmlDetailsFor: (state) => ({
      ...state,
      error: null,
      loading: true,
    }),
    discardTraceHashFor: (state) => ({
      ...state,
      error: null,
    }),
  },
});

export { userSlice };
